import {BaseCommand} from './base.command';
import {ReverseLinkSnapshot} from '../interfaces/command.interface';
import {CytoscapeService} from '../../cy-services/cytoscape.service';
import {NodeService} from '../../../api/services/node.service';
import {firstValueFrom} from 'rxjs';
import {UndoLabelMetadata} from '../undo-label.utils';
import {reverse} from 'lodash';

/**
 * ReverseLinkCommand handles reversing link direction.
 * Also used for changing link source/target.
 *
 * The command is pushed AFTER the reverse operation is already performed.
 * - execute() is only called on REDO
 * - undo() swaps direction back and reverses control points
 */
export class ReverseLinkCommand extends BaseCommand<ReverseLinkSnapshot> {
  constructor(
    private cyService: CytoscapeService,
    private nodeService: NodeService,
    snapshot: ReverseLinkSnapshot,
    part: string = 'Directionality'  // Can be 'Directionality', 'Source', or 'Target'
  ) {
    const labelMetadata: UndoLabelMetadata = {
      object: 'Link',
      verb: 'Change',
      part: part
    };

    super('reverse-link', `Link ${part} Change`, {before: snapshot, after: snapshot}, labelMetadata);
  }

  /**
   * Swap source/target and reverse control points
   */
  private async swapDirection(newSourceId: string, newTargetId: string): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const {linkId} = this.snapshot.after;
    const edge = cy.$(`#${linkId}`);
    if (edge.length === 0) return;

    // Get current edge data
    const edgeData = edge.data();
    const edgeType = edgeData.type || 'STRAIGHT';

    // Reverse control/bend points (mutates arrays but that's OK since we're swapping)
    if (edgeType === 'UNBUNDLED_BEZIER' && edgeData.controlPointPositions?.length > 0) {
      const reversed = reverse([...edgeData.controlPointPositions]);
      edge.data('controlPointPositions', reversed);
    } else if (edgeType === 'STRAIGHT' && edgeData.bendPointPositions?.length > 0) {
      const reversed = reverse([...edgeData.bendPointPositions]);
      edge.data('bendPointPositions', reversed);
    }

    // We need to remove and re-add to change source/target in Cytoscape
    const fullData = {...edge.data()};
    edge.remove();

    cy.add({
      group: 'edges',
      data: {
        ...fullData,
        source: newSourceId,
        target: newTargetId,
        from: {id: newSourceId},
        to: {id: newTargetId}
      }
    });

    // Reinitialize edge editing
    const newEdge = cy.$(`#${linkId}`);
    this.reinitializeEdgeHandles(cy, newEdge, fullData);

    // Update backend
    await firstValueFrom(
      this.nodeService.updateLink({
        id: linkId,
        from: {id: newSourceId},
        to: {id: newTargetId},
        controlPointPositions: fullData.controlPointPositions,
        bendPointPositions: fullData.bendPointPositions
      } as any)
    );

    cy.style().update();
  }

  async execute(): Promise<void> {
    // Called on REDO - swap to "after" state
    const {afterSourceId, afterTargetId} = this.snapshot.after;
    await this.swapDirection(afterSourceId, afterTargetId);
  }

  async undo(): Promise<void> {
    // Called on UNDO - swap back to "before" state
    const {beforeSourceId, beforeTargetId} = this.snapshot.before;
    await this.swapDirection(beforeSourceId, beforeTargetId);
  }

  private reinitializeEdgeHandles(cy: any, edge: any, edgeData: any): void {
    if (edge.length === 0) return;
    if (!(cy as any).edgeEditing) return;
    const edgeEditingInstance = (cy as any).edgeEditing('get');
    if (!edgeEditingInstance) return;

    const linkType = edgeData.type || 'STRAIGHT';

    edge.removeClass('edgebendediting-hasbendpoints');
    edge.removeClass('edgecontrolediting-hascontrolpoints');

    if (linkType === 'BEZIER') {
      edge.addClass('ignore_control_points');
    } else {
      edge.removeClass('ignore_control_points');
    }

    if (linkType === 'UNBUNDLED_BEZIER' && edgeData.controlPointPositions?.length > 0) {
      edge.addClass('edgecontrolediting-hascontrolpoints');
    } else if (linkType === 'STRAIGHT' && edgeData.bendPointPositions?.length > 0) {
      edge.addClass('edgebendediting-hasbendpoints');
    }

    const edgeEditingType = linkType === 'BEZIER' ? 'none' : linkType === 'STRAIGHT' ? 'bend' : 'control';
    edgeEditingInstance.initAnchorPoints([edge], edgeEditingType);
    this.cyService.redrawLinkLabelsAfterRender();
  }
}
